import moment from 'moment';

const filterDefaults = {
    checkInDate: moment(),
    checkOutDate: moment().add(6, 'days'),
    guests: 1,
    islands: ['0'], // '0' represents 'All' - will be set properly after islands load
    activityId: false,
    search: '',
    sorter: 'htol',
    tags: [],
    showEmpty: false,
    showAsTable: false
};

const TIMESLOT_MODES = ['always', 'never', 'default_on', 'default_off'];

/**
 * client_config.hideActivitiesWithoutTimeslot, off `window.clientData`
 * (the /clients/info payload). 'always'/'never' force the "hide activities
 * without timeslot" filter and drop the checkbox; 'default_on'/'default_off'
 * only seed it. Anything else (unset, stale) means 'default_on'.
 */
export function getTimeslotVisibility() {
    const mode = window.clientData?.hideActivitiesWithoutTimeslot;
    return TIMESLOT_MODES.includes(mode) ? mode : 'default_on';
}

/** True when the client forces the value, so the guest gets no checkbox. */
export function isTimeslotFilterLocked() {
    const mode = getTimeslotVisibility();
    return mode === 'always' || mode === 'never';
}

// showEmpty is the inverse of "hide activities without timeslot".
function resolveShowEmpty(param, hasStoredShowEmpty) {
    const mode = getTimeslotVisibility();
    if (mode === 'always') {
        return false;
    }
    if (mode === 'never') {
        return true;
    }
    // The guest picks: an explicit prior pick of THIS field (url or
    // localStorage) wins over the client's default, which only seeds a
    // first-time visitor. Checking "any filter is present" instead of this
    // field specifically would mean the seed gets clobbered by the first
    // unrelated filter change (date, search, island, ...).
    if (hasStoredShowEmpty) {
        return param === '1';
    }
    return mode === 'default_off';
}

export function getFilterFromUrl(urlSearchPart) {
    const savedFilters = window.localStorage.getItem('filters') || '';
    const searchParams = new URLSearchParams(
        urlSearchPart.trim() !== '' ? urlSearchPart : savedFilters
    );

    // treat dates a bit different, don't allow filters in the past
    let checkInDate = moment(searchParams.get('datefrom')).isValid()
        ? moment(searchParams.get('datefrom'))
        : moment();
    let checkOutDate = moment(searchParams.get('dateto')).isValid()
        ? moment(searchParams.get('dateto'))
        : moment().add(6, 'days');

    if (checkInDate.isBefore(moment())) {
        checkInDate = moment();
    }
    if (checkOutDate.isBefore(checkInDate)) {
        checkOutDate = moment(checkInDate).add(6, 'days');
    }

    // Parse islands from URL - can be comma-separated IDs
    let islandsParam = searchParams.get('island');
    let islands;

    if (islandsParam) {
        // Split by '-' and filter out empty strings
        islands = islandsParam.split('-').filter(e => e !== '');
    } else {
        // Let ActivityFilter set the default based on loaded islands
        islands = null;
    }

    return {
        checkInDate,
        checkOutDate,
        guests: parseInt(searchParams.get('guests'), 10) || 1,
        islands,
        activityId: parseInt(searchParams.get('activity'), 10) || false,
        search: searchParams.get('search') || '',
        sorter: searchParams.get('sorter') || 'ltoh',
        tags:
            searchParams
                .get('tags')
                ?.split('-')
                .filter(e => e !== '') || [],
        showEmpty: resolveShowEmpty(searchParams.get('showempty'), searchParams.has('showempty')),
        showAsTable: !!searchParams.get('showastable')
    };
}

export function getUrlFromFilter(filter, isAgent, isAdmin) {
    const search = new URLSearchParams();
    if (filter.checkInDate) {
        search.append('datefrom', filter.checkInDate.format('YYYY-MM-DD'));
    }
    if (filter.checkOutDate) {
        search.append('dateto', filter.checkOutDate.format('YYYY-MM-DD'));
    }
    if (filter.guests) {
        search.append('guests', filter.guests);
    }
    if (filter.islands && filter.islands.length > 0) {
        search.append('island', filter.islands.join('-'));
    }
    if (filter.activityId) {
        search.append('activity', filter.activityId);
    }
    if (filter.search && filter.search.length > 0) {
        search.append('search', filter.search);
    }
    if (filter.sorter) {
        search.append('sorter', filter.sorter);
    }
    if (filter.tags && filter.tags.length > 0) {
        search.append('tags', filter.tags.join('-'));
    }
    // Always written, even when false — resolveShowEmpty needs to tell an
    // explicit "off" apart from "the guest never touched this filter".
    search.append('showempty', filter.showEmpty ? '1' : '0');
    if (filter.showAsTable && !isAgent && !isAdmin) {
        search.delete('showastable');
    } else if (filter.showAsTable) {
        search.append('showastable', '0');
    }

    const searchString = search.toString();
    search.delete('activity');
    window.localStorage.setItem('filters', search.toString());
    return `?${searchString}`;
}

export function resetUrlFilters(keepDateRange = false) {
    const localFilters = {};
    if (keepDateRange) {
        const searchParams = new URLSearchParams(window.localStorage.getItem('filters'));
        const checkInDate = moment(searchParams.get('datefrom')).isValid()
            ? moment(searchParams.get('datefrom'))
            : moment();
        const checkOutDate = moment(searchParams.get('dateto')).isValid()
            ? moment(searchParams.get('dateto'))
            : moment().add(6, 'days');
        localFilters.checkInDate = checkInDate;
        localFilters.checkOutDate = checkOutDate;
    }

    // make sure the island is re-grabbed from the client data
    // Let ActivityFilter set the default based on loaded islands
    localFilters.islands = null;

    return getUrlFromFilter({ ...filterDefaults, ...localFilters }, false);
}

export const updateGuestDatesParams = (guest, callbackFn) => {
    const filters = localStorage.getItem('filters');
    const params = new URLSearchParams(filters);

    if (guest && guest.arrival_date && guest.departure_date) {
        if (filters && filters.search('datefrom') !== -1) {
            params.set('datefrom', moment(guest.arrival_date).format('YYYY-MM-DD'));
        }
        if (filters && filters.search('dateto') !== -1) {
            params.set('dateto', moment(guest.departure_date).format('YYYY-MM-DD'));
        }
    } else {
        params.set('datefrom', moment().format('YYYY-MM-DD'));
        params.set(
            'dateto',
            moment()
                .add(6, 'days')
                .format('YYYY-MM-DD')
        );
    }
    localStorage.setItem('filters', params.toString());
    callbackFn?.();
};

export const resetGuestDatesParams = callbackFn => {
    const filters = localStorage.getItem('filters');
    const params = new URLSearchParams(filters);
    params.set('datefrom', moment().format('YYYY-MM-DD'));
    params.set(
        'dateto',
        moment()
            .add(6, 'days')
            .format('YYYY-MM-DD')
    );
    localStorage.setItem('filters', params.toString());
    callbackFn?.();
};
